// useEmployeeFormLogic.ts
import { useState, useCallback } from "react";

import { Employee } from "0.Mine/3.Interfaces/Employee";
import { BusinessService } from "0.Mine/3.Interfaces/Services";

export interface EmployeeFormLogicReturn {
  employees: Employee[];
  showAddForm: boolean;
  handleAddEmployee: (employeeToAdd: Employee) => Promise<void>;
  handleUpdateEmployee: (updatedEmployee: Employee) => void;
  handleDeleteEmployee: (employeeId: number) => void;
  toggleAddForm: () => void;
  onFileUpload: (employeeId: number, file: File) => void;
}

export const useEmployeeFormLogicOnboarding = (
  initialEmployees: Employee[],
  updateBusinessData: (data: Employee[]) => void
): EmployeeFormLogicReturn => {
  const [employees, setEmployees] = useState<Employee[]>(initialEmployees);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);

  const handleAddEmployee = useCallback(
    async (employeeToAdd: Employee) => {
      setEmployees((prevEmployees) => {
        const updatedEmployees = [...prevEmployees, employeeToAdd];
        updateBusinessData(updatedEmployees);
        return updatedEmployees;
      });
      // Close the add form after a brief delay (for animation purposes)
      setTimeout(() => setShowAddForm(false), 300);
    },
    [updateBusinessData]
  );

  const handleUpdateEmployee = useCallback(
    (updatedEmployee: Employee) => {
      setEmployees((prevEmployees) => {
        const updatedEmployees = prevEmployees.map((emp) =>
          emp.id === updatedEmployee.id ? updatedEmployee : emp
        );
        updateBusinessData(updatedEmployees);
        return updatedEmployees;
      });
    },
    [updateBusinessData]
  );

  const handleDeleteEmployee = useCallback(
    (employeeId: number) => {
      setEmployees((prevEmployees) => {
        const updatedEmployees = prevEmployees.filter((emp) => emp.id !== employeeId);
        updateBusinessData(updatedEmployees);
        return updatedEmployees;
      });
    },
    [updateBusinessData]
  );

  const toggleAddForm = useCallback(() => {
    setShowAddForm((prev) => !prev);
  }, []);

  const onFileUpload = useCallback((employeeId: number, file: File) => {
    console.log(`File uploaded for employee ${employeeId}:`, file);
    //Find the employee and add the file to the employee object
    const updatedEmployees = employees.map((emp) => {
      if (emp.id === employeeId) {
        return { ...emp, image: file };
      }
      return emp;
    });
    updateBusinessData(updatedEmployees);
    setEmployees(updatedEmployees);
  }, []);

  return {
    employees,
    showAddForm,
    handleAddEmployee,
    handleUpdateEmployee,
    handleDeleteEmployee,
    toggleAddForm,
    onFileUpload,
  };
};
